import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowUpRight, Loader2, PartyPopper, Info } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  "Corporate Gifting & Merchandise",
  "Corporate Events & Experiences",
  "Exhibition & Expo Solutions",
  "Corporate Media & Content Production",
  "Branding, Creative, Printing & Production",
  "Corporate Travel & Destination Management",
  "Employee Engagement & Internal Communications",
  "Brand Activations & Experiential Marketing",
];

const INDUSTRIES = [
  "Energy / Oil & Gas",
  "Renewables / CleanTech",
  "Infrastructure / Industrial",
  "Climate / Sustainability",
  "Technology",
  "Professional Services",
  "Other",
];

const TIMELINES = [
  "Within 2 weeks",
  "Within 1 month",
  "1–3 months",
  "3–6 months",
  "More than 6 months",
  "Exploratory / not decided",
];

const CURRENCIES = ["INR (₹)", "USD ($)", "AED", "Other"];

const inputCls =
  "w-full rounded-xl border border-black/15 bg-white px-4 py-3 text-sm text-royal-deep placeholder:text-black/35 outline-none transition-all duration-300 focus:border-gold focus:shadow-[0_0_0_3px_rgba(224,19,39,0.15)]";

const labelCls = "block text-[13px] font-medium text-black/75 mb-2";

export default function EnquiryForm() {
  const [form, setForm] = useState({
    fullName: "",
    company: "",
    workEmail: "",
    phone: "",
    designation: "",
    industry: "",
    services: [],
    currency: "INR (₹)",
    budgets: {},
    timeline: "",
    location: "",
    preferredDate: "",
    message: "",
    consent: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      const service = e.detail;
      setForm((f) =>
        f.services.includes(service)
          ? f
          : { ...f, services: [...f.services, service], budgets: { ...f.budgets, [service]: "" } }
      );
    };
    window.addEventListener("oec:preselect", handler);
    return () => window.removeEventListener("oec:preselect", handler);
  }, []);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const toggleService = (s) => {
    setForm((f) => {
      const has = f.services.includes(s);
      const services = has ? f.services.filter((x) => x !== s) : [...f.services, s];
      const budgets = { ...f.budgets };
      if (has) delete budgets[s];
      else budgets[s] = "";
      return { ...f, services, budgets };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const payload = {
        full_name: form.fullName,
        company: form.company,
        work_email: form.workEmail,
        phone: form.phone,
        designation: form.designation,
        industry: form.industry,
        services: form.services,
        currency: form.currency,
        budgets: form.services.map((s) => ({ service: s, amount: form.budgets[s] || "" })),
        timeline: form.timeline,
        location: form.location,
        preferred_date: form.preferredDate,
        message: form.message,
        consent: form.consent,
      };
      const res = await fetch(`${API}/enquiry`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setSubmitted(true);
        toast.success("Enquiry sent — the OEC team will be in touch.");
      } else {
        const msg =
          typeof data.detail === "object"
            ? Object.values(data.detail)[0]
            : data.detail || "Something went wrong. Please try again.";
        toast.error(msg);
      }
    } catch {
      toast.error("Network error — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="enquiry" className="relative py-20 sm:py-28 scroll-mt-20 border-t border-black/10">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-5 gap-14">
        <div className="lg:col-span-2">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6 }}
          >
            <p className="font-mono text-[11px] sm:text-xs tracking-[0.3em] uppercase text-gold-dark">
              Let&rsquo;s talk
            </p>
            <h2 className="mt-4 font-display text-3xl sm:text-5xl font-bold tracking-tight text-royal-deep leading-[1.1]">
              Tell us what <span className="text-gold">you need.</span>
            </h2>
            <p className="mt-5 text-sm sm:text-base text-black/70 leading-relaxed">
              Complete the short enquiry form with your contact details, services, activity
              budgets and project brief. The more context you share, the easier it is for us to
              understand your requirement.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6, delay: 0.12 }}
            className="mt-9 rounded-2xl border border-black/10 bg-royal-soft p-6 card-sheen"
          >
            <p className="text-sm font-semibold text-royal-deep">What happens next?</p>
            <p className="mt-2.5 text-[13px] text-black/70 leading-relaxed">
              Your enquiry can be reviewed by the OEC team, who can contact you to clarify the
              brief and discuss a proposal. Please do not include confidential or sensitive
              information in this form.
            </p>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-3"
        >
          <div className="rounded-3xl border border-black/10 bg-white p-6 sm:p-10 shadow-[0_24px_70px_rgba(0,0,0,0.10)]">
            <AnimatePresence mode="wait">
              {submitted ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4 }}
                  data-testid="enquiry-success-message"
                  className="py-14 text-center"
                >
                  <span className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-gold-soft border border-gold/50 text-gold-dark">
                    <PartyPopper size={26} />
                  </span>
                  <h3 className="mt-6 font-display text-2xl font-bold text-royal-deep">
                    Enquiry received.
                  </h3>
                  <p className="mx-auto mt-3 max-w-md text-sm text-black/70 leading-relaxed">
                    Thank you — your brief is on its way to the OEC team. We&rsquo;ll review your
                    requirement and get in touch to discuss scope and a proposal.
                  </p>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0, y: -12 }}
                  onSubmit={submit}
                  data-testid="enquiry-form"
                >
                  <div className="flex items-center justify-between gap-4">
                    <h3 className="font-display text-xl font-semibold text-royal-deep">
                      Request a proposal
                    </h3>
                    <p className="hidden sm:block text-xs text-black/50">
                      Fields marked <span className="text-gold-dark">*</span> are required
                    </p>
                  </div>

                  <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className={labelCls} htmlFor="fullName">Full name <span className="text-gold-dark">*</span></label>
                      <input
                        id="fullName" required data-testid="enquiry-full-name-input"
                        className={inputCls} value={form.fullName}
                        onChange={(e) => set("fullName", e.target.value)} placeholder="Your name"
                      />
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="company">Company <span className="text-gold-dark">*</span></label>
                      <input
                        id="company" required data-testid="enquiry-company-input"
                        className={inputCls} value={form.company}
                        onChange={(e) => set("company", e.target.value)} placeholder="Company name"
                      />
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="workEmail">Work email <span className="text-gold-dark">*</span></label>
                      <input
                        id="workEmail" type="email" required data-testid="enquiry-email-input"
                        className={inputCls} value={form.workEmail}
                        onChange={(e) => set("workEmail", e.target.value)} placeholder="you@company.com"
                      />
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="phone">Phone number <span className="text-gold-dark">*</span></label>
                      <input
                        id="phone" type="tel" required data-testid="enquiry-phone-input"
                        className={inputCls} value={form.phone}
                        onChange={(e) => set("phone", e.target.value)} placeholder="+91 ..."
                      />
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="designation">Designation</label>
                      <input
                        id="designation" data-testid="enquiry-designation-input"
                        className={inputCls} value={form.designation}
                        onChange={(e) => set("designation", e.target.value)} placeholder="e.g. Marketing Lead"
                      />
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="industry">Industry</label>
                      <select
                        id="industry" data-testid="enquiry-industry-select"
                        className={`${inputCls} appearance-none`} value={form.industry}
                        onChange={(e) => set("industry", e.target.value)}
                      >
                        <option value="">Select industry</option>
                        {INDUSTRIES.map((i) => (
                          <option key={i} value={i}>{i}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="mt-7">
                    <p className={labelCls}>
                      Services required <span className="text-gold-dark">*</span>{" "}
                      <span className="text-black/50 font-normal">(select all that apply)</span>
                    </p>
                    <div className="flex flex-wrap gap-2.5" data-testid="enquiry-services-chips">
                      {SERVICES.map((s) => {
                        const active = form.services.includes(s);
                        return (
                          <button
                            key={s} type="button" data-testid={`enquiry-service-chip-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 42)}`}
                            onClick={() => toggleService(s)}
                            className={`rounded-full border px-4 py-2 text-[12px] font-medium transition-all duration-300 ${
                              active
                                ? "border-gold bg-gold-soft text-gold-dark shadow-[0_0_18px_rgba(224,19,39,0.2)]"
                                : "border-black/15 text-black/60 hover:border-gold hover:text-royal-deep"
                            }`}
                          >
                            {s}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <AnimatePresence>
                    {form.services.length > 0 && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.35, ease: "easeOut" }}
                        className="overflow-hidden"
                      >
                        <div className="mt-7 rounded-2xl border border-black/10 bg-royal-soft p-5">
                          <div className="flex flex-wrap items-center justify-between gap-3">
                            <p className="text-[13px] font-medium text-black/80">
                              Indicative budget for each selected service <span className="text-gold-dark">*</span>
                            </p>
                            <select
                              data-testid="enquiry-currency-select" aria-label="Budget currency"
                              className={`${inputCls} w-auto appearance-none py-2 text-xs`} value={form.currency}
                              onChange={(e) => set("currency", e.target.value)}
                            >
                              {CURRENCIES.map((c) => (
                                <option key={c} value={c}>{c}</option>
                              ))}
                            </select>
                          </div>
                          <p className="mt-1.5 text-xs text-black/50">
                            Choose the currency and approximate budget range for each activity.
                          </p>
                          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                            {form.services.map((s) => (
                              <div key={s} data-testid="enquiry-budget-field">
                                <label className="block text-xs text-black/60 mb-1.5" htmlFor={`budget-${s}`}>{s}</label>
                                <input
                                  id={`budget-${s}`} data-testid={`enquiry-budget-input-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 42)}`}
                                  className={inputCls} placeholder="e.g. 5–10 lakh / 10k–20k"
                                  value={form.budgets[s] || ""}
                                  onChange={(e) => setForm((f) => ({ ...f, budgets: { ...f.budgets, [s]: e.target.value } }))}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <div className="mt-7 grid grid-cols-1 sm:grid-cols-3 gap-5">
                    <div>
                      <label className={labelCls} htmlFor="timeline">Expected timeline</label>
                      <select
                        id="timeline" data-testid="enquiry-timeline-select"
                        className={`${inputCls} appearance-none`} value={form.timeline}
                        onChange={(e) => set("timeline", e.target.value)}
                      >
                        <option value="">Select timeline</option>
                        {TIMELINES.map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="location">Event / project location</label>
                      <input
                        id="location" data-testid="enquiry-location-input"
                        className={inputCls} value={form.location}
                        onChange={(e) => set("location", e.target.value)} placeholder="City / country"
                      />
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="preferredDate">Preferred date (if known)</label>
                      <input
                        id="preferredDate" type="date" data-testid="enquiry-date-input"
                        className={inputCls} value={form.preferredDate}
                        onChange={(e) => set("preferredDate", e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="mt-7">
                    <label className={labelCls} htmlFor="message">
                      Tell us about your requirement <span className="text-gold-dark">*</span>
                    </label>
                    <textarea
                      id="message" required rows={5} data-testid="enquiry-message-textarea"
                      className={`${inputCls} resize-y`} value={form.message}
                      onChange={(e) => set("message", e.target.value)}
                      placeholder="Objectives, audience, scale, anything else we should know..."
                    />
                  </div>

                  <label className="mt-7 flex items-start gap-3 cursor-pointer select-none">
                    <input
                      type="checkbox" required data-testid="enquiry-consent-checkbox"
                      className="mt-0.5 h-4 w-4 shrink-0 rounded border-black/25 bg-white accent-[#E01327]"
                      checked={form.consent}
                      onChange={(e) => set("consent", e.target.checked)}
                    />
                    <span className="text-[13px] text-black/75 leading-relaxed">
                      I agree to be contacted by One Energy Club about this enquiry.{" "}
                      <span className="text-gold-dark">*</span>
                    </span>
                  </label>

                  <button
                    type="submit" disabled={submitting}
                    data-testid="enquiry-submit-button"
                    className="group mt-8 inline-flex w-full sm:w-auto items-center justify-center gap-2.5 rounded-full bg-gold px-9 py-4 text-sm font-semibold text-white transition-all duration-300 hover:bg-gold-dark hover:shadow-[0_8px_36px_rgba(224,19,39,0.45)] disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting ? (
                      <>
                        Sending
                        <Loader2 size={16} className="animate-spin" />
                      </>
                    ) : (
                      <>
                        Submit enquiry
                        <ArrowUpRight size={17} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      </>
                    )}
                  </button>

                  <p className="mt-5 flex items-start gap-2 text-xs text-black/50 leading-relaxed">
                    <Info size={13} className="mt-0.5 shrink-0" />
                    Submitting this form is a request for follow-up, not a confirmed booking or
                    quotation.
                  </p>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
